// SPDX-License-Identifier: AGPL-3.0-or-later

import {VOICE_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {
	getCachedDesktopTroubleshootingSettings,
	getDesktopTroubleshootingSettings,
} from '@app/features/devtools/utils/DesktopTroubleshootingUtils';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import styles from '@app/features/user/components/modals/tabs/components/CodecSelectorSection.module.css';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	type CodecPreference,
	type CodecSupportInfo,
	getCodecCapabilityReport,
	getLiveKitSupportedCodecs,
	selectOptimalScreenShareCodec,
} from '@app/features/voice/utils/CodecCapabilityDetector';
import {
	getGpuEncoderReportSync,
	type HardwareEncodeReport,
	loadGpuEncoderReport,
} from '@app/features/voice/utils/GpuEncoderCapabilities';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type {VideoCodec} from 'livekit-client';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';

interface CodecSelectOption extends SelectOption<CodecPreference> {
	value: CodecPreference;
	label: string;
	isDisabled?: boolean;
	description?: string;
	badge?: 'recommended' | 'auto-pick';
	descriptionTone?: 'normal' | 'warn';
}
const CODEC_DISPLAY_LABEL: Record<VideoCodec, string> = {
	av1: 'AV1',
	vp9: 'VP9',
	vp8: 'VP8',
	h264: 'H.264',
	h265: 'H.265 (HEVC)',
};
function getSupportedCodecBlurb(codec: VideoCodec, i18n: ReturnType<typeof useLingui>['i18n']): string {
	switch (codec) {
		case 'av1':
			return i18n._(msg`Best compression. Hardware-accelerated only on RTX 40-series, RX 7000+, Intel Arc / Core Ultra.`);
		case 'vp9':
			return i18n._(msg`Strong quality at moderate CPU cost. Wide Chromium support.`);
		case 'vp8':
			return i18n._(msg`Universal fallback. Lowest quality but works everywhere.`);
		case 'h264':
			return i18n._(msg`Hardware-accelerated almost everywhere. Reliable for older systems.`);
		case 'h265':
			return i18n._(msg`HEVC. High quality at low bitrate, but requires hardware support.`);
	}
}
function hardwareSuffix(
	codec: VideoCodec,
	gpu: HardwareEncodeReport | null,
	i18n: ReturnType<typeof useLingui>['i18n'],
): string {
	if (!gpu) return '';
	switch (gpu[codec]) {
		case 'hardware':
			return ` ${i18n._(msg`Your GPU encodes this in hardware.`)}`;
		case 'software':
			return ` ${i18n._(msg`Your GPU has no hardware encoder for this — it will use your CPU.`)}`;
		case 'unknown':
			return '';
	}
}
export const CodecSelectorSection = observer(() => {
	const {t, i18n} = useLingui();
	const preference = VoiceSettings.getPreferredScreenShareCodec();
	const [gpuReport, setGpuReport] = useState<HardwareEncodeReport | null>(() => getGpuEncoderReportSync());
	const [hardwareAccelDisabled, setHardwareAccelDisabled] = useState<boolean | null>(
		() => getCachedDesktopTroubleshootingSettings()?.disableHardwareAcceleration ?? null,
	);
	useEffect(() => {
		if (gpuReport) return;
		let cancelled = false;
		void loadGpuEncoderReport().then((report) => {
			if (!cancelled) setGpuReport(report);
		});
		return () => {
			cancelled = true;
		};
	}, [gpuReport]);
	useEffect(() => {
		if (!isDesktop() || hardwareAccelDisabled !== null) return;
		let cancelled = false;
		void getDesktopTroubleshootingSettings().then((settings) => {
			if (!cancelled && settings) setHardwareAccelDisabled(settings.disableHardwareAcceleration);
		});
		return () => {
			cancelled = true;
		};
	}, [hardwareAccelDisabled]);
	const report = useMemo(() => getCodecCapabilityReport(), [gpuReport]);
	const liveKitCodecs = useMemo(() => getLiveKitSupportedCodecs(), []);
	const liveKitCodecCount = liveKitCodecs.length;
	const autoChosen = useMemo<VideoCodec>(() => selectOptimalScreenShareCodec('auto'), [gpuReport]);
	const options = useMemo<Array<CodecSelectOption>>(() => {
		const autoLabel = CODEC_DISPLAY_LABEL[autoChosen];
		const autoDescription = gpuReport?.gpuLabel
			? t`Picks the best codec your browser, system, and ${VOICE_PROVIDER_NAME} all support. Currently: ${autoLabel}, chosen for your ${gpuReport.gpuLabel}.`
			: t`Picks the best codec your browser, system, and ${VOICE_PROVIDER_NAME} all support. Currently: ${autoLabel}.`;
		const autoOption: CodecSelectOption = {
			value: 'auto',
			label: t`Auto`,
			description: autoDescription,
			badge: 'recommended',
		};
		const codecOptions = liveKitCodecs.map<CodecSelectOption>((codec) => {
			const support: CodecSupportInfo = report[codec];
			const description = support.supported
				? getSupportedCodecBlurb(codec, i18n) + hardwareSuffix(codec, gpuReport, i18n)
				: support.detail;
			const tone: 'normal' | 'warn' = !support.supported
				? 'warn'
				: support.hardwareAccelerated === 'software'
					? 'warn'
					: 'normal';
			return {
				value: codec,
				label: CODEC_DISPLAY_LABEL[codec],
				isDisabled: !support.supported,
				description,
				descriptionTone: tone,
				badge: codec === autoChosen ? 'auto-pick' : undefined,
			};
		});
		return [autoOption, ...codecOptions];
	}, [autoChosen, gpuReport, liveKitCodecs, report, t]);
	const renderRow = useCallback(
		(option: CodecSelectOption, compact: boolean, isSelected: boolean) => {
			let badgeLabel: string | null = null;
			if (option.badge === 'recommended') {
				badgeLabel = t`Recommended`;
			} else if (option.badge === 'auto-pick' && !compact) {
				badgeLabel = t`Auto would pick this`;
			}
			return (
				<div className={clsx(styles.codecOptionRow, compact && styles.codecOptionRowCompact)}>
					<div className={styles.codecLabelRow}>
						<span className={clsx(styles.codecLabel, option.isDisabled && styles.codecLabelMuted)}>{option.label}</span>
						{badgeLabel ? (
							<span
								className={clsx(
									styles.codecBadge,
									option.badge === 'recommended' && styles.codecBadgeRecommended,
									isSelected && !compact && styles.codecBadgeOnSelectedRow,
								)}
							>
								{badgeLabel}
							</span>
						) : null}
					</div>
					{!compact && option.description ? (
						<span
							className={clsx(
								styles.codecDescription,
								option.descriptionTone === 'warn' && styles.codecDescriptionWarn,
								isSelected && styles.codecDescriptionOnSelectedRow,
							)}
						>
							{option.description}
						</span>
					) : null}
				</div>
			);
		},
		[t],
	);
	const renderOption = useCallback(
		(option: CodecSelectOption, isSelected: boolean) => renderRow(option, false, isSelected),
		[renderRow],
	);
	const renderValue = useCallback(
		(option: CodecSelectOption | null) => (option ? renderRow(option, true, false) : null),
		[renderRow],
	);
	const handleChange = useCallback((value: CodecPreference) => {
		VoiceSettingsCommands.update({preferredScreenShareCodec: value});
	}, []);
	const openAdvancedTab = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		ComponentDispatch.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'advanced'});
	}, []);
	const hardwareAccelOff = isDesktop() && hardwareAccelDisabled === true;
	return (
		<div className={styles.codecPicker}>
			<Select<CodecPreference, false, CodecSelectOption>
				label={t`Screen Share Video Codec`}
				value={preference}
				options={options}
				onChange={handleChange}
				renderOption={renderOption}
				renderValue={renderValue}
				isSearchable={false}
			/>
			{hardwareAccelOff ? (
				<p className={clsx(styles.codecHint, styles.codecHintWarn)}>
					<Trans>
						Hardware acceleration is currently disabled, so every codec will encode on CPU regardless of your GPU.{' '}
						<button type="button" className={styles.codecAdvancedLink} onClick={openAdvancedTab}>
							Turn it back on in Advanced
						</button>
						.
					</Trans>
				</p>
			) : null}
			<p className={styles.codecHint}>
				{t`Auto picks ${CODEC_DISPLAY_LABEL.av1} when your GPU can hardware-encode it, falls back to ${CODEC_DISPLAY_LABEL.vp9} or ${CODEC_DISPLAY_LABEL.h264} otherwise, and finally to ${CODEC_DISPLAY_LABEL.vp8}. Unsupported codecs are disabled with a reason; ${VOICE_PROVIDER_NAME} only accepts ${liveKitCodecCount} codecs.`}
			</p>
			{isDesktop() && !hardwareAccelOff ? (
				<p className={styles.codecHint}>
					<Trans>
						Hardware acceleration must be on for any codec to use your GPU. You can toggle it under{' '}
						<button type="button" className={styles.codecAdvancedLink} onClick={openAdvancedTab}>
							Advanced
						</button>
						.
					</Trans>
				</p>
			) : null}
		</div>
	);
});
